import { calculateLoanTotal } from "@/lib/loanInterest";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

import {
  fmtMoney,
  fmtDate,
  todayISO,
} from "@/lib/format";

import {
  printCustomerProfile,
  printReceipt,
} from "@/lib/print";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  ArrowLeft,
  Printer,
  Trash2,
  Wallet,
  CheckCircle2,
  Clock,
  AlertCircle,
  MessageCircle,
  RefreshCw,
} from "lucide-react";

import { toast } from "sonner";


export default function LoanDetail() {

  const { id } = useParams();
  const navigate = useNavigate();

  const {
    settings,
    business,
    currency,
    dateFormat,
  } = useSettings();


  // ==========================================================
  // STATE
  // ==========================================================

  const [loan, setLoan] = useState(null);

  const [payOpen, setPayOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  const [editingDueWeek, setEditingDueWeek] = useState(null);
  const [dueDateDraft, setDueDateDraft] = useState("");
  const [dueDateSaving, setDueDateSaving] = useState(false);

  const [editingPaidWeek, setEditingPaidWeek] = useState(null);
  const [paidDateDraft, setPaidDateDraft] = useState("");
  const [paidDateSaving, setPaidDateSaving] = useState(false);

  const [renewalOpen, setRenewalOpen] =
    useState(false);

  const [renewalSaving, setRenewalSaving] =
    useState(false);


  const [payForm, setPayForm] = useState({
    week: null,
    amount: "",
    method: "Cash",
    paid_date: todayISO(),
  });


  const [renewalForm, setRenewalFormState] =
    useState({
      principal: "",
      total_repayment: "",
      start_date: todayISO(),
      duration_weeks: 10,
    });


  // ==========================================================
  // LOAD LOAN
  // ==========================================================

  const load = useCallback(async () => {

    try {

      const response =
        await api.get(`/loans/${id}`);

      setLoan(response.data);

    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to load loan"
        )
      );

    }

  }, [id]);


  useEffect(() => {

    load();

  }, [load]);


  // ==========================================================
  // EDIT EMI DUE DATE
  // ==========================================================

  const saveEmiDueDate = async (installment) => {

    if (!dueDateDraft) {
      toast.error("Select a due date");
      return;
    }

    setDueDateSaving(true);

    try {

      await api.put(
        `/loans/${id}/schedule/${installment.week}/due-date`,
        {
          due_date: dueDateDraft,
        }
      );

      toast.success("EMI due date updated successfully");

      setEditingDueWeek(null);
      setDueDateDraft("");

      await load();

    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to update EMI due date"
        )
      );

    } finally {

      setDueDateSaving(false);

    }
  };


  // ==========================================================
  // EDIT EMI PAID DATE
  // ==========================================================

  const saveEmiPaidDate = async (installment) => {

    if (!paidDateDraft) {
      toast.error("Select a paid date");
      return;
    }

    setPaidDateSaving(true);

    try {

      await api.put(
        `/loans/${id}/schedule/${installment.week}/paid-date`,
        {
          paid_date: paidDateDraft,
        }
      );

      toast.success(
        "Paid date updated successfully"
      );

      setEditingPaidWeek(null);
      setPaidDateDraft("");

      await load();

    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to update paid date"
        )
      );

    } finally {

      setPaidDateSaving(false);

    }
  };


  // ==========================================================
  // PAYMENT METHODS
  // ==========================================================

  const methods =
    settings?.payment_methods ||
    ["Cash", "UPI"];


  // ==========================================================
  // OPEN PAYMENT
  // ==========================================================

  const openPay = (installment) => {

    const remaining = Math.max(
      0,
      Number(
        installment.amount || 0
      ) -
        Number(
          installment.paid_amount || 0
        )
    );

    setPayForm({

      week:
        installment.week,

      amount:
        remaining.toFixed(2),

      method:
        methods[0] || "Cash",

      paid_date:
        todayISO(),

    });

    setPayOpen(true);
  };


  // ==========================================================
  // WHATSAPP EMI REMINDER
  // ==========================================================

  const sendEmiReminder = (
    installment
  ) => {

    const customerName =
      loan?.customer?.name ||
      "Customer";

    const phone = (
      loan?.customer?.phone ||
      ""
    ).replace(
      /\D/g,
      ""
    );

    if (!phone) {

      toast.error(
        "Customer phone number is missing"
      );

      return;
    }


    let whatsappPhone = phone;

    if (phone.length === 10) {

      whatsappPhone =
        `91${phone}`;
    }


    const remainingAmount =
      Math.max(
        0,
        Number(
          installment.amount || 0
        ) -
          Number(
            installment.paid_amount ||
              0
          )
      );


    const businessName =
      business?.business_name ||
      settings?.business_name ||
      "Akash Finance";


    const message =
      `Dear ${customerName},\n\n` +

      `This is a reminder regarding your EMI.\n\n` +

      `Loan No: ${
        loan?.loan_number || "—"
      }\n` +

      `Week: ${
        installment.week
      }\n` +

      `EMI Amount Due: ${
        fmtMoney(
          remainingAmount,
          currency
        )
      }\n` +

      `Due Date: ${
        fmtDate(
          installment.due_date,
          dateFormat
        )
      }\n\n` +

      `Please make the payment on or before the due date.\n\n` +

      `Thank you.\n` +

      `${businessName}`;


    const whatsappUrl =
      `https://wa.me/${whatsappPhone}` +
      `?text=${encodeURIComponent(
        message
      )}`;


    window.open(
      whatsappUrl,
      "_blank"
    );
  };


  // ==========================================================
  // RECORD PAYMENT
  // ==========================================================

  const recordPayment = async () => {

    const amount =
      parseFloat(
        payForm.amount
      );


    if (
      !amount ||
      amount <= 0
    ) {

      toast.error(
        "Enter a valid amount"
      );

      return;
    }


    if (!payForm.week) {

      toast.error(
        "Invalid EMI week"
      );

      return;
    }


    if (!payForm.paid_date) {

      toast.error(
        "Select payment date"
      );

      return;
    }


    setSaving(true);


    try {

      const response =
        await api.post(
          `/loans/${id}/payments`,
          {
            week:
              payForm.week,

            amount:
              amount,

            method:
              payForm.method,

            paid_date:
              payForm.paid_date,
          }
        );


      toast.success(
        "Payment recorded successfully"
      );


      setPayOpen(false);


      await load();


      const receipt = {

        ...response.data.payment,

        loan:
          response.data.loan,

        customer:
          loan.customer,

      };


      if (
        window.confirm(
          "Payment saved. Print the EMI receipt now?"
        )
      ) {

        printReceipt(
          receipt,
          business,
          settings
        );
      }


    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to record payment"
        )
      );

    } finally {

      setSaving(false);

    }
  };


  // ==========================================================
  // OPEN RENEWAL
  // ==========================================================

  const setRenewalForm = (next) => setRenewalFormState((previous) => {
    const value = typeof next === "function" ? next(previous) : next;
    return { ...value, frequency: value.frequency || settings?.default_frequency || "weekly",
      total_repayment: calculateLoanTotal(value.principal, settings?.default_interest_rate ?? 30) };
  });
  useEffect(() => {
    if (!renewalOpen || !settings) return;
    setRenewalFormState((previous) => ({ ...previous,
      frequency: settings.default_frequency || "weekly",
      total_repayment: calculateLoanTotal(previous.principal, settings.default_interest_rate ?? 30) }));
  }, [renewalOpen, settings?.default_interest_rate, settings?.default_frequency]);

  const openRenewal = () => {

    setRenewalForm({

      principal:
        loan?.principal || "",

      total_repayment:
        loan?.total_repayment || "",

      start_date:
        todayISO(),

      duration_weeks:
        10,

    });

    setRenewalOpen(true);
  };


  // ==========================================================
  // RENEW LOAN
  // ==========================================================

  const renewLoan = async () => {

    const principal =
      parseFloat(
        renewalForm.principal
      );


    const totalRepayment =
      parseFloat(
        renewalForm.total_repayment
      );


    const durationWeeks =
      Number(
        renewalForm.duration_weeks
      );


    if (
      !principal ||
      principal <= 0
    ) {

      toast.error(
        "Enter a valid new loan amount"
      );

      return;
    }


    if (
      !totalRepayment ||
      totalRepayment <= 0
    ) {

      toast.error(
        "Enter a valid total repayment"
      );

      return;
    }


    if (
      totalRepayment < principal
    ) {

      toast.error(
        "Total repayment cannot be less than loan amount"
      );

      return;
    }


    if (
      !renewalForm.start_date
    ) {

      toast.error(
        "Select renewal date"
      );

      return;
    }


    if (
      !durationWeeks ||
      durationWeeks <= 0
    ) {

      toast.error(
        "Enter a valid number of weeks"
      );

      return;
    }


    // --------------------------------------------------------
    // CONFIRMATION
    // --------------------------------------------------------

    const oldOutstanding =
      Number(
        loan?.outstanding || 0
      );


    const newLoanAmount =
      principal;


    const amountGiven =
      Math.max(
        0,
        newLoanAmount -
          oldOutstanding
      );


    const weeklyEmi =
      totalRepayment /
      durationWeeks;


    const confirmed =
      window.confirm(
        `Renew this loan?\n\n` +

        `Old Outstanding: ${fmtMoney(
          oldOutstanding,
          currency
        )}\n` +

        `New Loan Amount: ${fmtMoney(
          newLoanAmount,
          currency
        )}\n` +

        `Amount Given to Customer: ${fmtMoney(
          amountGiven,
          currency
        )}\n\n` +

        `New Total Repayment: ${fmtMoney(
          totalRepayment,
          currency
        )}\n` +

        `Weekly EMI: ${fmtMoney(
          weeklyEmi,
          currency
        )}\n\n` +

        `The old loan will be marked as RENEWED.`
      );


    if (!confirmed) {

      return;
    }


    setRenewalSaving(true);


    try {

      const response =
        await api.post(
          `/loans/${id}/renew`,
          {
            frequency: renewalForm.frequency || settings?.default_frequency || "weekly",
            principal:
              principal,

            total_repayment:
              totalRepayment,

            start_date:
              renewalForm.start_date,

            duration_weeks:
              durationWeeks,
          }
        );


      toast.success(
        "Loan renewed successfully"
      );


      setRenewalOpen(false);


      // ------------------------------------------------------
      // OPEN NEW LOAN
      // ------------------------------------------------------

      if (
        response.data?.new_loan?.id
      ) {

        navigate(
          `/loan/${response.data.new_loan.id}`
        );

      } else {

        await load();

      }


    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to renew loan"
        )
      );

    } finally {

      setRenewalSaving(false);

    }
  };


  // ==========================================================
  // PRINT INDIVIDUAL RECEIPT
  // ==========================================================

  const printOneReceipt = (
    installment
  ) => {

    const payment = {

      receipt_no:
        installment.receipt_no,

      week:
        installment.week,

      amount:
        installment.paid_amount,

      method:
        installment.method,

      paid_date:
        installment.paid_date,

      remaining_balance:
        loan.outstanding,

      total_paid:
        loan.total_paid,

      loan:
        loan,

      customer:
        loan.customer,

    };


    printReceipt(
      payment,
      business,
      settings
    );
  };


  // ==========================================================
  // DELETE LOAN
  // ==========================================================

  const removeLoan = async () => {

    const confirmed =
      window.confirm(
        "Delete this loan?"
      );


    if (!confirmed) {

      return;
    }


    try {

      await api.delete(
        `/loans/${id}`
      );


      toast.success(
        "Loan deleted"
      );


      navigate(
        `/customers/${loan.customer_id}`
      );


    } catch (error) {

      toast.error(
        apiError(
          error.response?.data?.detail ||
            "Unable to delete loan"
        )
      );

    }
  };


  // ==========================================================
  // LOADING
  // ==========================================================

  if (!loan) {

    return (
      <div className="text-slate-400">
        Loading…
      </div>
    );
  }


  // ==========================================================
  // STAT CARD
  // ==========================================================

  const stat = (
    label,
    value,
    className = "text-navy"
  ) => (

    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">

      <div className="text-xs uppercase tracking-wider text-slate-500">
        {label}
      </div>

      <div
        className={`mt-1 font-mono text-lg font-bold ${className}`}
      >
        {value}
      </div>

    </div>
  );


  // ==========================================================
  // STATUS BADGE
  // ==========================================================

  const badge = (
    installment
  ) => {

    // --------------------------------------------------------
    // PAID
    // --------------------------------------------------------

    if (
      installment.status ===
      "paid"
    ) {

      return (

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">

          <CheckCircle2 className="h-3 w-3" />

          Paid

        </span>
      );
    }


    // --------------------------------------------------------
    // ADJUSTED
    // --------------------------------------------------------

    if (
      installment.status ===
      "adjusted"
    ) {

      return (

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">

          <RefreshCw className="h-3 w-3" />

          Adjusted

        </span>
      );
    }


    // --------------------------------------------------------
    // PARTIAL
    // --------------------------------------------------------

    if (
      installment.status ===
      "partial"
    ) {

      return (

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">

          Partial

        </span>
      );
    }


    // --------------------------------------------------------
    // RENEWED LOAN
    // --------------------------------------------------------

    if (
      loan.lifecycle_status ===
      "renewed"
    ) {

      return (

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">

          <RefreshCw className="h-3 w-3" />

          Adjusted

        </span>
      );
    }


    // --------------------------------------------------------
    // OVERDUE / PENDING
    // --------------------------------------------------------

    let overdue = false;

    try {

      overdue =
        new Date(
          installment.due_date
        ) < new Date();

    } catch {

      overdue = false;

    }


    if (overdue) {

      return (

        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">

          <AlertCircle className="h-3 w-3" />

          Overdue

        </span>
      );
    }


    return (

      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">

        <Clock className="h-3 w-3" />

        Pending

      </span>
    );
  };


  // ==========================================================
  // CHECK IF LOAN IS RENEWED
  // ==========================================================

  const isRenewed =
    loan.lifecycle_status ===
    "renewed";


  // ==========================================================
  // RENEWAL EMI PREVIEW
  // ==========================================================

  const renewalWeeklyEmi =
    Number(
      renewalForm.total_repayment ||
        0
    ) /
    Number(
      renewalForm.duration_weeks ||
        10
    );


  // ==========================================================
  // OLD OUTSTANDING
  // ==========================================================

  const oldOutstanding =
    Number(
      loan.outstanding || 0
    );


  // ==========================================================
  // RETURN
  // ==========================================================

  return (

    <div
      className="space-y-6"
      data-testid="loan-detail-page"
    >


      {/* =====================================================
          BACK
      ===================================================== */}

      <button
        onClick={() =>
          navigate(
            `/customers/${loan.customer_id}`
          )
        }
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy"
        data-testid="back-to-customer"
      >

        <ArrowLeft className="h-4 w-4" />

        Back to{" "}

        {loan.customer?.name}

      </button>


      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

        <div>

          <div className="flex items-center gap-3 flex-wrap">

            <h1 className="font-heading text-2xl font-bold text-navy">

              Loan{" "}

              {loan.loan_number}

            </h1>


            {/* LOAN STATUS */}

            {isRenewed ? (

              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">

                <RefreshCw className="h-3 w-3" />

                Renewed

              </span>

            ) : (

              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">

                Active

              </span>

            )}

          </div>


          <p className="text-slate-500">

            {loan.customer?.name}

            {" · "}

            started{" "}

            {fmtDate(
              loan.start_date,
              dateFormat
            )}

          </p>


          {/* RENEWAL INFORMATION */}

          {isRenewed &&
            loan.renewed_to_loan_number && (

              <p className="text-sm text-purple-600 mt-1">

                Renewed into loan{" "}

                <button
                  className="font-semibold underline"
                  onClick={() =>
                    navigate(
                      `/loan/${loan.renewed_to_loan_id}`
                    )
                  }
                >
                  {loan.renewed_to_loan_number}
                </button>

              </p>

            )}

        </div>


        {/* HEADER BUTTONS */}

        <div className="flex flex-wrap gap-2">


          {/* PRINT */}

          <Button
            variant="outline"
            onClick={() =>
              printCustomerProfile(
                {
                  ...loan.customer,
                  loans: [loan],
                },
                business,
                settings
              )
            }
            data-testid="print-loan-statement-button"
          >

            <Printer className="h-4 w-4 mr-2" />

            Print Statement

          </Button>


          {/* RENEW */}

          {!isRenewed && (

            <Button
              variant="outline"
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              onClick={openRenewal}
              data-testid="renew-loan-button"
            >

              <RefreshCw className="h-4 w-4 mr-2" />

              Renew Loan

            </Button>

          )}


          {/* DELETE */}

          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={removeLoan}
            data-testid="delete-loan-button"
          >

            <Trash2 className="h-4 w-4" />

          </Button>

        </div>

      </div>


      {/* =====================================================
          RENEWAL NOTICE
      ===================================================== */}

      {isRenewed && (

        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">

          <div className="flex items-start gap-3">

            <RefreshCw className="h-5 w-5 text-purple-600 mt-0.5" />

            <div>

              <div className="font-semibold text-purple-800">

                This loan has been renewed

              </div>

              <div className="text-sm text-purple-700 mt-1">

                The remaining balance of this old loan
                was adjusted into the new loan.
                It will not generate overdue EMIs.

              </div>

            </div>

          </div>

        </div>

      )}


      {/* =====================================================
          LOAN STATISTICS
      ===================================================== */}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">

        {stat(
          "Loan Amount",
          fmtMoney(
            loan.principal,
            currency
          )
        )}


        {stat(
          "Total Repayment",
          fmtMoney(
            loan.total_repayment,
            currency
          )
        )}


        {stat(
          loan.frequency === "monthly" ? "Monthly EMI" : "Weekly EMI",
          fmtMoney(
            loan.weekly_emi,
            currency
          )
        )}


        {stat(
          "Paid",
          fmtMoney(
            loan.total_paid,
            currency
          ),
          "text-emerald-700"
        )}


        {stat(
          "Outstanding",
          fmtMoney(
            loan.outstanding,
            currency
          ),
          isRenewed
            ? "text-purple-700"
            : "text-maroon"
        )}

      </div>


      {/* =====================================================
          RENEWAL ACCOUNTING
      ===================================================== */}

      {isRenewed && (

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {stat(
            "Old Balance Adjusted",
            fmtMoney(
              loan.renewal_outstanding_adjusted ||
                loan.original_outstanding_at_renewal ||
                0,
              currency
            ),
            "text-purple-700"
          )}


          {stat(
            "New Loan",
            fmtMoney(
              loan.renewal_new_loan_principal ??
                loan.principal,
              currency
            ),
            "text-navy"
          )}


          {stat(
            "Amount Given",
            fmtMoney(
              loan.amount_given_to_customer ??
                Math.max(
                  0,
                  Number(
                    loan.renewal_new_loan_principal ??
                      loan.principal ??
                      0
                  ) -
                  Number(loan.old_outstanding || 0)
                ),
              currency
            ),
            "text-emerald-700"
          )}

        </div>

      )}


      {/* =====================================================
          REPAYMENT SCHEDULE
      ===================================================== */}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">


        <div className="px-5 py-4 border-b border-slate-100">

          <div className="flex items-center justify-between gap-3 flex-wrap">

            <h2 className="font-heading text-lg font-semibold text-navy">

              {loan.duration_weeks}

              -{loan.frequency === "monthly" ? "Month" : "Week"} Repayment Schedule

            </h2>


            {isRenewed && (

              <span className="text-xs text-purple-600">

                Old loan schedule

              </span>

            )}

          </div>

        </div>


        <div className="overflow-x-auto">

          <table className="w-full text-sm">

            <thead>

              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">

                <th className="px-5 py-3">
                  Week
                </th>

                <th className="px-5 py-3">
                  Due Date
                </th>

                <th className="px-5 py-3 text-right">
                  EMI
                </th>

                {loan?.renewed_from_loan_id && (
                  <>
                    <th className="px-5 py-3 text-right">
                      Principal
                    </th>

                    <th className="px-5 py-3 text-right">
                      Old Interest
                    </th>

                    <th className="px-5 py-3 text-right">
                      New Interest
                    </th>
                  </>
                )}

                <th className="px-5 py-3 text-right">
                  Paid
                </th>

                <th className="px-5 py-3">
                  Paid Date
                </th>

                <th className="px-5 py-3">
                  Method
                </th>

                <th className="px-5 py-3">
                  Status
                </th>

                <th className="px-5 py-3 text-right">
                  Action
                </th>

              </tr>

            </thead>


            <tbody>

              {(
                loan.schedule || []
              ).map(
                (installment) => (

                  <tr
                    key={
                      installment.week
                    }
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      installment.status ===
                      "adjusted"
                        ? "bg-purple-50/40"
                        : ""
                    }`}
                    data-testid={`emi-row-${installment.week}`}
                  >


                    {/* WEEK */}

                    <td className="px-5 py-3 font-semibold">

                      {installment.week}

                    </td>


                    {/* DUE DATE */}

                    <td className="px-5 py-3 text-slate-500">

                      
                        {editingDueWeek === installment.week ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={dueDateDraft}
                              onChange={(e) =>
                                setDueDateDraft(e.target.value)
                              }
                              className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700"
                              disabled={dueDateSaving}
                            />

                            <button
                              type="button"
                              className="rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                              onClick={() =>
                                saveEmiDueDate(installment)
                              }
                              disabled={dueDateSaving}
                            >
                              {dueDateSaving ? "Saving..." : "Save"}
                            </button>

                            <button
                              type="button"
                              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              onClick={() => {
                                setEditingDueWeek(null);
                                setDueDateDraft("");
                              }}
                              disabled={dueDateSaving}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>
                              {fmtDate(
                                installment.due_date,
                                dateFormat
                              )}
                            </span>

                            {!isRenewed && (
                              <button
                                type="button"
                                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-accentblue hover:bg-slate-50"
                                onClick={() => {
                                  setEditingDueWeek(installment.week);
                                  setDueDateDraft(
                                    String(
                                      installment.due_date || ""
                                    ).slice(0, 10)
                                  );
                                }}
                                title="Edit EMI Due Date"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        )}
                      </td>


                    {/* EMI */}

                    <td className="px-5 py-3 text-right font-mono">

                      {fmtMoney(
                        installment.amount,
                        currency
                      )}

                    </td>


                    {/* RENEWAL COMPONENTS */}

                    {loan?.renewed_from_loan_id && (
                      <>
                        <td className="px-5 py-3 text-right font-mono">

                          {fmtMoney(
                            installment.principal_due || 0,
                            currency
                          )}

                        </td>

                        <td className="px-5 py-3 text-right font-mono">

                          {fmtMoney(
                            installment.old_interest_due || 0,
                            currency
                          )}

                        </td>

                        <td className="px-5 py-3 text-right font-mono">

                          {fmtMoney(
                            installment.new_interest_due || 0,
                            currency
                          )}

                        </td>
                      </>
                    )}


                    {/* PAID */}

                    <td className="px-5 py-3 text-right font-mono">

                      {fmtMoney(
                        installment.paid_amount || 0,
                        currency
                      )}

                    </td>

                    {/* PAID DATE */}

                    <td className="px-5 py-3 text-slate-500">

                      {String(
                        installment.status || ""
                      ).toLowerCase() === "paid" &&
                      installment.paid_date ? (

                        editingPaidWeek === installment.week ? (

                          <div className="flex items-center gap-2">

                            <input
                              type="date"
                              value={paidDateDraft}
                              onChange={(e) =>
                                setPaidDateDraft(
                                  e.target.value
                                )
                              }
                              className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700"
                              disabled={paidDateSaving}
                            />

                            <button
                              type="button"
                              className="rounded-md bg-green-600 px-2 py-1 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                              onClick={() =>
                                saveEmiPaidDate(
                                  installment
                                )
                              }
                              disabled={paidDateSaving}
                            >
                              {paidDateSaving
                                ? "Saving..."
                                : "Save"}
                            </button>

                            <button
                              type="button"
                              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              onClick={() => {
                                setEditingPaidWeek(
                                  null
                                );
                                setPaidDateDraft("");
                              }}
                              disabled={paidDateSaving}
                            >
                              Cancel
                            </button>

                          </div>

                        ) : (

                          <div className="flex items-center gap-2">

                            <span>
                              {fmtDate(
                                installment.paid_date,
                                dateFormat
                              )}
                            </span>

                            <button
                              type="button"
                              className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-accentblue hover:bg-slate-50"
                              onClick={() => {
                                setEditingPaidWeek(
                                  installment.week
                                );

                                setPaidDateDraft(
                                  String(
                                    installment.paid_date ||
                                      ""
                                  ).slice(
                                    0,
                                    10
                                  )
                                );
                              }}
                              title="Edit Paid Date"
                            >
                              Edit
                            </button>

                          </div>

                        )

                      ) : installment.paid_date ? (

                        fmtDate(
                          installment.paid_date,
                          dateFormat
                        )

                      ) : (

                        "—"

                      )}

                    </td>


                    {/* METHOD */}

                    <td className="px-5 py-3">

                      {installment.method ||
                        "—"}

                    </td>


                    {/* STATUS */}

                    <td className="px-5 py-3">

                      {badge(
                        installment
                      )}

                    </td>


                    {/* ACTION */}
{/* PRINT PDF - PAID EMI ONLY */}
{String(installment.status || "").toLowerCase() === "paid" && (
  <button
    type="button"
    className="ml-2 inline-flex items-center rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
    onClick={() => {
      const w = window.open("", "_blank");
      if (!w) return;
      const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({
        "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"
      }[c]));
      w.document.write(`
        <html>
          <head>
            <title>EMI Payment Receipt</title>
            <style>
              body{font-family:Arial,sans-serif;padding:40px;color:#222}
              .receipt{max-width:600px;margin:auto;border:1px solid #ddd;padding:30px}
              h2{text-align:center;margin-bottom:25px}
              .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #eee}
              .label{font-weight:bold}
            </style>
          </head>
          <body>
            <div class="receipt">
              <h2>EMI Payment Receipt</h2>
              <div class="row"><span class="label">Loan No</span><span>${esc(loan.loan_number)}</span></div>
              <div class="row"><span class="label">EMI</span><span>${esc(installment.week)}</span></div>
              <div class="row"><span class="label">Paid Amount</span><span>${esc(installment.paid_amount || 0)}</span></div>
              <div class="row"><span class="label">Paid Date</span><span>${esc(installment.paid_date || "")}</span></div>
              <div class="row"><span class="label">Status</span><span>PAID</span></div>
            </div>
          </body>
        </html>
      `);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    }}
  >
    Print PDF
  </button>
)}


                    <td className="px-5 py-3 text-right">


                      {/* ADJUSTED */}

                      {installment.status ===
                      "adjusted" ? (

                        <span className="text-xs text-purple-600 font-medium">

                          Adjusted in renewal

                        </span>

                      ) :


                      /* RENEWED LOAN */

                      isRenewed ? (

                        <span className="text-xs text-purple-600 font-medium">

                          Closed by renewal

                        </span>

                      ) :


                      /* PAID */

                      installment.status ===
                      "paid" ? (

                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-accentblue"
                          onClick={() =>
                            printOneReceipt(
                              installment
                            )
                          }
                          data-testid={`print-receipt-${installment.week}`}
                        >

                          <Printer className="h-4 w-4" />

                        </Button>

                      ) : (

                        <div className="flex items-center justify-end gap-2">


                          {/* WHATSAPP */}

                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() =>
                              sendEmiReminder(
                                installment
                              )
                            }
                            title="Send EMI Reminder on WhatsApp"
                            data-testid={`emi-reminder-${installment.week}`}
                          >

                            <MessageCircle className="h-4 w-4" />

                          </Button>


                          {/* PAY */}

                          <Button
                            size="sm"
                            className="bg-maroon hover:bg-maroon-hover"
                            onClick={() =>
                              openPay(
                                installment
                              )
                            }
                            data-testid={`pay-emi-${installment.week}`}
                          >

                            <Wallet className="h-4 w-4 mr-1" />

                            Pay

                          </Button>

                        </div>

                      )}

                    </td>

                  </tr>

                )
              )}

            </tbody>

          </table>

        </div>

      </div>


      {/* =====================================================
          RECORD PAYMENT DIALOG
      ===================================================== */}

      <Dialog
        open={payOpen}
        onOpenChange={
          setPayOpen
        }
      >

        <DialogContent
          data-testid="record-payment-dialog"
        >

          <DialogHeader>

            <DialogTitle className="font-heading">

              Record EMI Payment —

              {" "}

              Week{" "}

              {payForm.week}

            </DialogTitle>

          </DialogHeader>


          <div className="grid grid-cols-2 gap-4">


            {/* AMOUNT */}

            <div>

              <Label>
                Amount ({currency})
              </Label>

              <Input
                type="number"
                min="0"
                step="0.01"
                value={
                  payForm.amount
                }
                onChange={(event) =>
                  setPayForm({
                    ...payForm,
                    amount:
                      event.target.value,
                  })
                }
                data-testid="payment-amount-input"
              />

            </div>


            {/* DATE */}

            <div>

              <Label>
                Paid Date
              </Label>

              <Input
                type="date"
                value={
                  payForm.paid_date
                }
                onChange={(event) =>
                  setPayForm({
                    ...payForm,
                    paid_date:
                      event.target.value,
                  })
                }
                data-testid="payment-date-input"
              />

            </div>


            {/* METHOD */}

            <div className="col-span-2">

              <Label>
                Payment Method
              </Label>

              <Select
                value={
                  payForm.method
                }
                onValueChange={(
                  value
                ) =>
                  setPayForm({
                    ...payForm,
                    method:
                      value,
                  })
                }
              >

                <SelectTrigger
                  data-testid="payment-method-select"
                >

                  <SelectValue />

                </SelectTrigger>


                <SelectContent>

                  {methods.map(
                    (method) => (

                      <SelectItem
                        key={method}
                        value={method}
                      >

                        {method}

                      </SelectItem>

                    )
                  )}

                </SelectContent>

              </Select>

            </div>

          </div>


          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setPayOpen(
                  false
                )
              }
            >

              Cancel

            </Button>


            <Button
              className="bg-maroon hover:bg-maroon-hover"
              onClick={
                recordPayment
              }
              disabled={
                saving
              }
              data-testid="record-emi-payment-button"
            >

              {saving
                ? "Saving…"
                : "Record Payment"}

            </Button>

          </DialogFooter>

        </DialogContent>

      </Dialog>


      {/* =====================================================
          RENEW LOAN DIALOG
      ===================================================== */}

      <Dialog
        open={renewalOpen}
        onOpenChange={
          setRenewalOpen
        }
      >

        <DialogContent
          data-testid="renew-loan-dialog"
        >

          <DialogHeader>

            <DialogTitle className="font-heading">

              🔄 Renew Loan

            </DialogTitle>

          </DialogHeader>


          <div className="space-y-4">


            {/* OLD LOAN */}

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">

              <div className="text-sm text-slate-500">

                Customer

              </div>

              <div className="font-semibold text-navy">

                {loan.customer?.name}

              </div>


              <div className="text-sm text-slate-500 mt-2">

                Old Loan:

                {" "}

                {loan.loan_number}

              </div>


              <div className="text-sm text-slate-500">

                Old Outstanding:

                {" "}

                <span className="font-semibold text-red-600">

                  {fmtMoney(
                    oldOutstanding,
                    currency
                  )}

                </span>

              </div>

            </div>


            <div><Label>Repayment Frequency</Label><select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3" value={renewalForm.frequency || settings?.default_frequency || "weekly"} onChange={(e) => setRenewalForm({ ...renewalForm, frequency: e.target.value })}><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></div>
            {/* NEW LOAN AMOUNT */}

            <div>

              <Label>

                New Loan Amount ({currency})

              </Label>

              <Input
                type="number"
                min="0"
                step="0.01"
                value={
                  renewalForm.principal
                }
                onChange={(event) =>
                  setRenewalForm({
                    ...renewalForm,
                    principal:
                      event.target.value,
                  })
                }
                placeholder="Enter new loan amount"
                data-testid="renewal-principal-input"
              />

            </div>


            {/* TOTAL REPAYMENT */}

            <div>

              <Label>

                New Total Repayment ({currency})

              </Label>

              <Input
                readOnly type="number"
                min="0"
                step="0.01"
                value={
                  renewalForm.total_repayment
                }
                onChange={(event) =>
                  setRenewalForm({
                    ...renewalForm,
                    total_repayment:
                      event.target.value,
                  })
                }
                placeholder="Enter total repayment"
                data-testid="renewal-total-repayment-input"
              />

            </div>


            {/* DATE */}

            <div>

              <Label>
                Renewal Date
              </Label>

              <Input
                type="date"
                value={
                  renewalForm.start_date
                }
                onChange={(event) =>
                  setRenewalForm({
                    ...renewalForm,
                    start_date:
                      event.target.value,
                  })
                }
                data-testid="renewal-date-input"
              />

            </div>


            {/* WEEKS */}

            <div>

              <Label>

                Repayment Period ({renewalForm.frequency === "monthly" ? "Months" : "Weeks"})

              </Label>

              <Input
                type="number"
                min="1"
                value={
                  renewalForm.duration_weeks
                }
                onChange={(event) =>
                  setRenewalForm({
                    ...renewalForm,
                    duration_weeks:
                      event.target.value,
                  })
                }
                data-testid="renewal-duration-input"
              />

              <p className="text-xs text-slate-500 mt-1">

                The new EMI schedule
                will be created
                automatically {renewalForm.frequency === "monthly" ? "every month from the start date." : "every Friday."}

              </p>

            </div>


            {/* =================================================
                RENEWAL CALCULATION
            ================================================= */}

            {renewalForm.principal &&
              renewalForm.total_repayment &&
              renewalForm.duration_weeks && (

                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 space-y-3">


                  <div>

                    <div className="text-xs text-slate-500">

                      Old Outstanding

                    </div>

                    <div className="font-mono font-bold text-red-600">

                      {fmtMoney(
                        oldOutstanding,
                        currency
                      )}

                    </div>

                  </div>


                  <div>

                    <div className="text-xs text-slate-500">

                      New Loan Amount

                    </div>

                    <div className="font-mono font-bold text-navy">

                      {fmtMoney(
                        Number(
                          renewalForm.principal
                        ) || 0,
                        currency
                      )}

                    </div>

                  </div>


                  <div className="border-t border-emerald-200 pt-3">

                    <div className="text-xs text-emerald-700">

                      Amount Given to Customer

                    </div>

                    <div className="font-mono text-xl font-bold text-emerald-800">

                      {fmtMoney(
                        Math.max(
                          0,
                          (
                            Number(
                              renewalForm.principal
                            ) || 0
                          ) -
                          oldOutstanding
                        ),
                        currency
                      )}

                    </div>

                    <div className="text-xs text-emerald-700 mt-1">

                      New loan amount − old outstanding

                    </div>

                  </div>


                  <div className="border-t border-emerald-200 pt-3">

                    <div className="text-xs text-emerald-700">

                      Weekly EMI

                    </div>

                    <div className="font-mono font-bold text-emerald-800">

                      {fmtMoney(
                        renewalWeeklyEmi,
                        currency
                      )}

                    </div>

                    <div className="text-xs text-emerald-700 mt-1">

                      Every Friday

                    </div>

                  </div>

                </div>

              )}

          </div>


          <DialogFooter>

            <Button
              variant="outline"
              onClick={() =>
                setRenewalOpen(
                  false
                )
              }
              disabled={
                renewalSaving
              }
            >

              Cancel

            </Button>


            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={
                renewLoan
              }
              disabled={
                renewalSaving
              }
              data-testid="confirm-renew-loan-button"
            >

              {renewalSaving
                ? "Renewing…"
                : "Confirm Renewal"}

            </Button>

          </DialogFooter>

        </DialogContent>

      </Dialog>

    </div>
  );
}
